import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig, loginRequest, isMsalConfigured } from "../config/authConfig";

let msalInstance = null;
let isInitialized = false;

const LOGIN_HINT_KEY = 'mfa_login_hint';

/**
 * Sign-in hint (UPN) handed over by the Intune automation as #hint=… – with several Microsoft
 * accounts in the browser, the silent sign-in fails without it (AADSTS16000). Call before MSAL
 * starts: keeps the hint for this window and removes it from the address (never sent to the server).
 */
export function captureLoginHint() {
  const match = window.location.hash.match(/^#hint=([^&]+)$/);
  if (!match) return;
  try {
    const hint = decodeURIComponent(match[1]);
    if (/^[^@\s]+@[^@\s]+$/.test(hint)) window.sessionStorage.setItem(LOGIN_HINT_KEY, hint);
  } catch (_) {}
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

function withLoginHint(request) {
  let loginHint = null;
  try {
    loginHint = window.sessionStorage.getItem(LOGIN_HINT_KEY);
  } catch (_) {}
  return loginHint ? { ...request, loginHint } : request;
}

/**
 * Initializes the MSAL instance (singleton pattern)
 */
export async function getMsalInstance() {
  if (!isMsalConfigured()) {
    return null;
  }

  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }

  if (!isInitialized) {
    await msalInstance.initialize();
    
    // Handle redirect promise if user is returning from a redirect login
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response && response.account) {
        msalInstance.setActiveAccount(response.account);
      } else {
        const currentAccounts = msalInstance.getAllAccounts();
        if (currentAccounts.length > 0) {
          msalInstance.setActiveAccount(currentAccounts[0]);
        }
      }
    } catch (redirectError) {
      console.error("Error handling redirect promise:", redirectError);
    }

    // Set active account on login success
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        msalInstance.setActiveAccount(event.payload.account);
      }
    });

    isInitialized = true;
  }

  return msalInstance;
}

/**
 * Signs in the user using seamless full-page redirect (no popups).
 * redirectStartPage: where MSAL navigates after the sign-in has been processed on the
 * registered redirect URI (the site root) – e.g. back to /setup; defaults to the root.
 */
export async function signIn({ redirectStartPage } = {}) {
  const instance = await getMsalInstance();
  if (!instance) {
    throw new Error("MSAL is not configured.");
  }

  // Seamless redirect instead of popup window
  await instance.loginRedirect(withLoginHint(redirectStartPage ? { ...loginRequest, redirectStartPage } : loginRequest));
}

/**
 * Tries to sign in without any user interaction (SSO via the existing Windows / Edge session).
 * Returns the account on success, otherwise null so the caller can fall back to signIn().
 */
export async function trySilentSignIn() {
  // MSAL runs the silent sign-in in a hidden iframe that loads this site again – never start another one in there
  if (window.self !== window.top) return null;

  const instance = await getMsalInstance();
  if (!instance) return null;

  try {
    const response = await instance.ssoSilent(withLoginHint(loginRequest));
    instance.setActiveAccount(response.account);
    return response.account;
  } catch (error) {
    console.warn("Silent SSO not possible, interactive sign-in required.", error);
    return null;
  }
}

/**
 * Signs out the current active account using redirect
 */
export async function signOut() {
  const instance = await getMsalInstance();
  if (!instance) return;

  const activeAccount = instance.getActiveAccount() || (instance.getAllAccounts().length > 0 ? instance.getAllAccounts()[0] : null);
  if (activeAccount) {
    await instance.logoutRedirect({
      account: activeAccount,
      postLogoutRedirectUri: window.location.origin
    });
  } else {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = window.location.origin;
  }
}

/**
 * Acquires an access token silently for Microsoft Graph.
 * allowRedirect: false for background checks that must never leave the page – they get the error instead.
 */
export async function getAccessToken({ allowRedirect = true } = {}) {
  const instance = await getMsalInstance();
  if (!instance) {
    throw new Error("MSAL is not configured.");
  }

  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  if (!account) {
    throw new Error("No active account found. Please sign in.");
  }

  const request = {
    ...loginRequest,
    account: account
  };

  try {
    const response = await instance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    if (!allowRedirect) throw error;
    console.warn("Silent token acquisition failed. Acquiring via redirect...", error);
    await instance.acquireTokenRedirect(request);
  }
}

/**
 * Gets currently logged in account details
 */
export async function getCurrentAccount() {
  const instance = await getMsalInstance();
  if (!instance) return null;
  return instance.getActiveAccount() || (instance.getAllAccounts().length > 0 ? instance.getAllAccounts()[0] : null);
}
